<template>
  <form v-if="sourceInfo">
    <v-text-field
      v-model="sourceInfo.name"
      label="Name"
      outlined
      dense
      required
      name="name"
      :disabled="state.loading || state.saveLoading"
    ></v-text-field>
    <v-autocomplete
      :items="state.accountList"
      v-model="accountId"
      :loading="state.accountListLoading || state.saveLoading"
      placeholder="Account Id"
      label="Account Id"
      :item-title="(item) => `${item.name} ( ${item.displayName} )`"
      item-value="name"
    ></v-autocomplete>
    <v-autocomplete
      :items="state.propertyList"
      v-model="sourceInfo.data.propertyId"
      :loading="state.propertyListLoading || state.accountListLoading || state.saveLoading"
      placeholder="Property Id"
      label="Property Id"
      :item-title="(item) => `${item.name} ( ${item.displayName} )`"
      item-value="name"
    ></v-autocomplete>

    <div class="align-center d-flex justify-end flex-column-reverse flex-sm-row flex-wrap">
      <v-btn
        :loading="state.loading"
        variant="elevated"
        color="grey"
        class="mt-4 mt-sm-0 w-100 w-sm-auto"
        :to="URLs.PAGE_CUSTOMER_EDIT(customerId)"
        :disabled="state.saveLoading"
        text="Cancel"
      />
      <v-btn
        :loading="state.saveLoading"
        @click="handleClickSave"
        class="ml-0 ml-sm-2 w-100 w-sm-auto"
        color="primary"
        variant="elevated"
        append-icon="mdi-content-save"
        text="Save"
      />
    </div>
  </form>
  <Loading message="Loading source info..." v-else />
</template>

<script setup lang="ts">
const props = defineProps<{
  accountId: string;
}>();

import { useRouter } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";
import { Google, IApp } from "@/types";
import { useAccountStore } from "@/stores/account";
import { makeAlert } from "@/utils";
import { URLs } from "@/constants";
import Loading from "../Widgets/Loading.vue";
import _ from "lodash";
import useRouteParams from "@/hooks/useRouteParams";
import { useAuthStore } from "@/stores/auth";
import GoogleAnalytics from "@/actions/GoogleAnalytics";
const router = useRouter();
const accountStore = useAccountStore();
const authStore = useAuthStore();
const user = computed(() => authStore.user);

const { sourceId, customerId } = useRouteParams();
const sourceInfo = ref<IApp.IGoogleAnalyticsAccount>({
  name: "",
  data: {},
  account: "",
  customerId: customerId.value!,
  feed: IApp.IFeedTypes.GoogleAnalytics,
  status: IApp.IAccountStatus.Active,
  syncs: []
});

const accountId = ref<string>();

const state = ref<{
  businessId: string;
  apiKey: string;
  name: string;
  loading: boolean;
  saveLoading: boolean;
  accountListLoading: boolean;
  propertyListLoading: boolean;
  accountList: IApp.IGoogleAnalyticsAdminAccount[];
  propertyList: Google.GoogleAnalyticsAdminAccountProperty[];
}>({
  businessId: "",
  apiKey: "",
  name: "",
  loading: false,
  saveLoading: false,
  accountListLoading: false,
  propertyListLoading: false,
  accountList: [],
  propertyList: []
});

const fetchAccountInfo = async (
  userId: string | undefined,
  customerId: string | undefined,
  sourceId: string | undefined
) => {
  if (userId && sourceId && customerId) {
    state.value.loading = true;
    const response = await accountStore.fetchAccountInfo<IApp.IFeedTypes.GoogleAnalytics>(
      userId,
      customerId,
      sourceId
    );
    if (response.status) {
      if (response.data.account.feed === IApp.IFeedTypes.GoogleAnalytics) {
        sourceInfo.value = response.data.account;
        accountId.value = response.data.account.data.accountId;
        await fetchPropertyList(
          userId,
          customerId,
          sourceId,
          response.data.account.data.accountId!
        );
      } else {
        makeAlert("This account is not a Google Analytics account", "error");
        state.value.loading = false;
        router.push({
          name: "profile"
        });
      }
    } else {
      makeAlert(response.message, "error");
    }
    state.value.loading = false;
  }
};

const handleClickSave = async () => {
  if (sourceInfo.value && confirm("Are you sure you want to save?")) {
    state.value.saveLoading = true;
    const response = await accountStore.patchAccountInfo(
      user.value?.id!,
      customerId.value!,
      sourceInfo.value.account,
      {
        type: IApp.IFeedTypes.GoogleAnalytics,
        accountId: accountId.value,
        propertyId: sourceInfo.value.data.propertyId,
        name: sourceInfo.value.name
      }
    );
    if (response.status) {
      makeAlert(response.data.message, "success");
    } else {
      makeAlert(response.message, "error");
    }
    state.value.saveLoading = false;
  }
};

onMounted(async () => {
  fetchAccountInfo(user.value?.id, customerId.value, sourceId.value);
  if (user.value && customerId.value && sourceId.value) {
    state.value.accountListLoading = true;
    state.value.accountList =
      (await GoogleAnalytics.getAccountList(user.value.id, customerId.value, sourceId.value)).data
        ?.accounts ?? [];
    state.value.accountListLoading = false;
  }
});
const fetchPropertyList = _.debounce(
  async (userId: string, customerId: string, sourceId: string, accountId: string) => {
    if (sourceInfo.value && accountId) {
      state.value.propertyListLoading = true;
      GoogleAnalytics.getAccountPropertyList(userId, customerId, sourceId, accountId)
        .then((response) => {
          state.value.propertyList = response.data?.properties ?? [];
          state.value.propertyListLoading = false;
          if (
            !_.find(
              response.data?.properties,
              (item) => item.name === sourceInfo.value.data.propertyId
            )
          ) {
            sourceInfo.value.data.propertyId = undefined;
          }
        })
        .catch(() => {
          state.value.propertyListLoading = false;
          state.value.propertyList = [];
        });
    }
  },
  500
);

watch(
  accountId,
  (newValue) => {
    if (sourceInfo.value) {
      if (newValue) {
        fetchPropertyList(user.value?.id!, customerId.value!, sourceId.value!, newValue);
      } else {
        state.value.propertyList = [];
      }
    }
  },
  { deep: true }
);
</script>
